import { Button, IconButton, Skeleton, Stack, TableCell, Tooltip, Typography } from '@mui/material';
import { AssignedUsersCells, assignUser, deleteUser, getAssignedUsers, getUsers, getUsersWithoutDoctors, unassignUser } from 'framework/user';
import useFetchSection from 'hooks/useFetchSection';
import List from '../List';
import EmptyContent from 'components/EmptyContent';
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import { IAssignedUsers, IUser } from 'models/user';
import { useEffect, useState } from 'react';
import { useAlert } from 'contexts/alertContext';
import { QueryClient, useMutation } from 'react-query';
import EditModalReseller from './partials/Edit';
import { IFormField } from 'models/app';
import { InputTypes } from 'constants/enums';
import { handleError } from 'helpers/utils';
import { Delete } from '@mui/icons-material';
import DeleteModal from '../Form/partials/Delete';

interface Props {
    slugId: string
}
function AssignUserList({ slugId }: Props) {

    const [loading2, setLoading2] = useState(false)
    const [inputObj, setInputObj] = useState<{ doctorId: number, patientId: number }>({} as { doctorId: number, patientId: number });
    const [openEdit, setOpenEdit] = useState<boolean>(false);
    const [selectedId, setSelectedId] = useState<number | null>(null)
    const [openDelete, setOpenDelete] = useState<boolean>(false);
    const handleChange = (name: string, value: any) => {
        setInputObj((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const { setAlert } = useAlert();

    const handleOpenEdit = () => {
        setOpenEdit(true);
    };

    const handleOpenDelete = (id: number) => {
        setOpenDelete(true);
        setSelectedId(id)
    };

    const assignUserMutation = useMutation({
        mutationFn: (patientID: number) => {
            return assignUser({ doctorID: +slugId!, patientID });
        },
    });

    const unassignUserMutation = useMutation({
        mutationFn: (patientID: number) => {
            return unassignUser({ doctorID: +slugId!, patientID });
        },
    });

    const queryClient = new QueryClient();
    const [users, setUsers] = useState<IUser[]>([])
    const handleClose = () => {
        setOpenEdit(false)
        setOpenDelete(false)
        setInputObj({} as { patientId: number, doctorId: number })
    }

    const {
        loading,
        data: Assigned,
        error,
        revalidate,
    } = useFetchSection({
        fetchData: () => getAssignedUsers(slugId),
        fetchPermission: async () => null,
        section: "Assigned Users"
    });

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await queryClient.fetchQuery("get Users list without doctor", () => getUsersWithoutDoctors('without-doctor'));
                setUsers(response.data)
            } catch (error) {
                console.error('Failed to fetch dropdown data:', error);
            }
        };

        fetchUsers();
    }, []);

    if (loading)
        return (
            <Stack spacing={2} p={2}>
                {Array.from({ length: 10 }).map((_, i) => (
                    <Skeleton variant="rectangular" key={i} width="100%" height={30} />
                ))}
            </Stack>
        );


    const getBodyCells = (row: IAssignedUsers) => {
        let cells = Object.keys(row).map((key: string, i: number) => {
            if (key === "firstName")
                return {
                    id: "name",
                    element: (
                        <TableCell align={"left"} key={i}>
                            <Typography variant="body2">{`${row?.firstName} ${row?.lastName}`}</Typography>
                        </TableCell>
                    ),
                };
            if (key === "email")
                return {
                    id: "email",
                    element: (
                        <TableCell align={"left"} key={i}>
                            <Typography variant="body2">{row?.email}</Typography>
                        </TableCell>
                    ),
                };

            if (key === "contactNumber")
                return {
                    id: "contactNumber",
                    element: (
                        <TableCell align={"left"} key={i}>
                            <Typography variant="body2">{row?.contactNumber}</Typography>
                        </TableCell>
                    ),
                };


            if (key === "patientID")
                return {
                    id: "action",
                    element: (
                        <TableCell align={"left"} key={i}>
                            <Stack
                                direction={"row"}
                                alignContent={"center"}
                                spacing={1}
                            >
                                <Tooltip title={"Unassign"}>
                                    <IconButton
                                        size={"small"}
                                        sx={{
                                            color: "red",
                                        }}
                                        onClick={() => handleOpenDelete(row.patientID)}
                                    >
                                        <Delete />
                                    </IconButton>
                                </Tooltip>


                            </Stack>
                        </TableCell>
                    ),
                };
        });
        return cells;
    };

    const filteredUsers = users.filter(user =>
        !(Assigned || []).some(assigned => assigned.patientID === user.userId)
    );

    let countriesOptions = filteredUsers.map((user) => { return { value: user?.userId.toString(), label: (user.firstName + user?.lastName).toString() } });


    let editBasicFields: IFormField[] = [
        {
            name: 'patientId',
            label: 'Users',
            type: InputTypes.SELECT,
            options: countriesOptions,
            placeholder: 'Select user',
            internalLabel: true,
            required: true
        }
    ];

    const handleAssign = async () => {
        try {
            setLoading2(true)
            await assignUserMutation.mutateAsync(+inputObj.patientId);
            setAlert({
                open: true,
                message: `User assigned successfully`,
                type: "success",
            });
            setLoading2(false)
            handleClose()
            revalidate();
        } catch (error: any) {
            setAlert({
                open: true,
                message: handleError(error) || "Something went wrong",
                type: "error",
            });
            setLoading2(false)
        }

    };


    const handleUnassign = async () => {
        try {
            setLoading2(true)
            await unassignUserMutation.mutateAsync(selectedId!);
            setAlert({
                open: true,
                message: `User unassigned successfully`,
                type: "success",
            });
            setLoading2(false)
            handleClose()
            revalidate();
        } catch (error: any) {
            setAlert({
                open: true,
                message: handleError(error) || "Something went wrong",
                type: "error",
            });
            setLoading2(false)
        }

    };

    const AssignedSafe = Assigned || []
    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBlock: 10 }}> <Button onClick={handleOpenEdit} variant='contained'>Assign a User</Button> </div>

            {AssignedSafe!.length > 0 && (
                <List
                    headCells={AssignedUsersCells}
                    data={AssignedSafe!}
                    getBodyCells={getBodyCells}
                    canEdit={false}
                />
            )}
            {(!AssignedSafe || AssignedSafe.length === 0) &&

                <EmptyContent
                    icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
                    header={"No Assigned Users"}
                    message={`No Assigned User Yet`}
                />
            }

            {openEdit && (
                <EditModalReseller
                    loading={loading2}
                    open={openEdit}
                    title={`Assign User`}
                    basicFields={editBasicFields}
                    onClose={handleClose}
                    onSubmit={handleAssign}
                    onChanges={handleChange}
                />
            )}

            {openDelete && (
                <DeleteModal
                    loading={loading2}
                    open={openDelete}
                    title={`Unassign User`}
                    buttonText='Unassign'
                    description='Are you sure to unassign this user?'
                    onClose={handleClose}
                    onSubmit={handleUnassign}
                />
            )}
        </div>
    )
}

export default AssignUserList